import { Box, Menu, Text, UnstyledButton, Divider, rem, Flex, Loader } from '@mantine/core';
import { IconLock, IconLogout, IconMinus, IconCircleFilled, IconChevronDown } from '@tabler/icons-react';
import { useNavigate, Link } from 'react-router';
import { useMedplum, ResourceAvatar } from '@medplum/react';
import { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { useMediaQuery } from '@mantine/hooks';
import { ConditionalLayout } from '../ConditionalLayout';
import { createReference } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { ProviderAvailabilityStatus } from '@bariendo/lib/constants';
import {
  getProviderAvailabilityStatus,
  setProviderAvailabilityStatus,
  AVAILABILITY_STATUS_LABELS,
  AVAILABILITY_STATUS_COLORS,
} from '../../services/provider-availability';
import { alert } from '../../services/alert';
import { useTwilioPhone } from '../../hooks/useTwilioPhone';
import { clearHeldInsuranceCards } from '../Timeline/TimelineItems/PatientAccessCall/useInsuranceCard';

export function UserMenu({ profile }: { profile: Practitioner }) {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { disconnect, connectionStatus, connect } = useTwilioPhone();
  const [status, setStatus] = useState<ProviderAvailabilityStatus>(ProviderAvailabilityStatus.ONLINE);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [targetStatus, setTargetStatus] = useState<ProviderAvailabilityStatus | null>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Helper function to convert theme color strings to CSS variables
  const getColorVar = (colorString: string): string => {
    const [colorName, shade] = colorString.split('.');
    return `var(--mantine-color-${colorName}-${shade})`;
  };

  useEffect(() => {
    if (!profile?.id) return;

    const loadAvailabilityStatus = async () => {
      try {
        setIsLoadingStatus(true);
        const providerRef = createReference(profile);
        const currentStatus = await getProviderAvailabilityStatus(medplum, providerRef);
        setStatus(currentStatus);
      } catch {
        // Default to ONLINE on error
        setStatus(ProviderAvailabilityStatus.ONLINE);
      } finally {
        setIsLoadingStatus(false);
      }
    };

    loadAvailabilityStatus();
  }, [medplum, profile]);

  // Auto-connect to Twilio when user is logged in and connection is offline
  useEffect(() => {
    if (medplum.isAuthenticated() && connectionStatus === 'offline' && status === ProviderAvailabilityStatus.ONLINE) {
      // eslint-disable-next-line no-console
      console.log('twilio connection status is offline. Attempting to auto-connect with Twilio');
      connect();
    }
  }, [medplum, connectionStatus, connect, status]);

  const handleStatusChange = async (newStatus: ProviderAvailabilityStatus) => {
    if (!profile?.id || isChangingStatus || newStatus === status) return;

    try {
      setIsChangingStatus(true);
      setTargetStatus(newStatus);
      // Wait for backend confirmation before updating UI
      await setProviderAvailabilityStatus(medplum, newStatus);

      setStatus(newStatus);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error setting provider availability status:', error);
      alert.error({
        title: 'Availability status update failed',
        message: 'Could not update availability status',
      });
      const providerRef = createReference(profile);
      const currentStatus = await getProviderAvailabilityStatus(medplum, providerRef);
      setStatus(currentStatus);
    } finally {
      setIsChangingStatus(false);
      setTargetStatus(null);
    }
  };

  const handleSignOut = async () => {
    try {
      await disconnect();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error disconnecting from Twilio on sign out:', error);
    }
    medplum.signOut();
    // Insurance card details typed on a PN card and not yet saved anywhere.
    clearHeldInsuranceCards();
    posthog.reset();
    navigate('/signin');
  };

  const fullName = [profile?.name?.[0]?.given?.[0], profile?.name?.[0]?.family].filter(Boolean).join(' ') || 'User';

  return (
    <Flex gap={isMobile ? 'xs' : 'sm'} align="center" pl="sm">
      <Box pos="relative" w={32} h={32}>
        <ResourceAvatar value={profile} size="32" radius="xl" />
        {isChangingStatus ? (
          <Box
            pos="absolute"
            top={0}
            right={0}
            w={10}
            h={10}
            style={{
              transform: 'translate(25%, -25%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Loader size={10} color="gray" />
          </Box>
        ) : (
          <Box
            pos="absolute"
            top={0}
            right={0}
            w={10}
            h={10}
            bg={AVAILABILITY_STATUS_COLORS[status]}
            style={{
              borderRadius: '50%',
              border: '2px solid white',
              transform: 'translate(25%, -25%)',
            }}
          />
        )}
      </Box>

      <ConditionalLayout inlineCondition={isMobile} gap="0" miw={isMobile ? 50 : 80}>
        <Text display="block" fw={600} size={isMobile ? 'xs' : 'xs'}>
          {fullName}
        </Text>
        {isChangingStatus ? (
          <Box h={isMobile ? 14 : 17} />
        ) : (
          <Text display="block" c="dimmed" size={isMobile ? 'xxs' : 'xs'}>
            {AVAILABILITY_STATUS_LABELS[status]}
          </Text>
        )}
      </ConditionalLayout>

      {/* Chevron is now the only clickable Menu trigger */}
      <Menu width={220} withArrow shadow="md" withinPortal>
        <Menu.Target>
          <UnstyledButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: rem(8),
              transition: 'background-color 150ms ease',
            }}
          >
            <IconChevronDown size={isMobile ? 12 : 16} />
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          <Box px="sm" py="xs">
            <Text size="xs" c="dimmed">
              {profile?.telecom?.[0]?.value || '—'}
            </Text>
          </Box>

          <Divider />

          <Menu.Item
            leftSection={
              isChangingStatus && targetStatus === ProviderAvailabilityStatus.ONLINE ? (
                <Loader size={16} />
              ) : (
                <IconCircleFilled
                  size={16}
                  color={getColorVar(AVAILABILITY_STATUS_COLORS[ProviderAvailabilityStatus.ONLINE])}
                />
              )
            }
            onClick={() => handleStatusChange(ProviderAvailabilityStatus.ONLINE)}
            disabled={isLoadingStatus || isChangingStatus}
          >
            <Text size="xs">{AVAILABILITY_STATUS_LABELS[ProviderAvailabilityStatus.ONLINE]}</Text>
          </Menu.Item>

          <Menu.Item
            leftSection={
              isChangingStatus && targetStatus === ProviderAvailabilityStatus.DO_NOT_DISTURB ? (
                <Loader size={16} />
              ) : (
                <Box pos="relative" w={16} h={16}>
                  <IconCircleFilled
                    size={16}
                    color={getColorVar(AVAILABILITY_STATUS_COLORS[ProviderAvailabilityStatus.DO_NOT_DISTURB])}
                  />
                  <IconMinus
                    size={10}
                    color="white"
                    stroke={4}
                    style={{
                      position: 'absolute',
                      top: '55%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </Box>
              )
            }
            onClick={() => handleStatusChange(ProviderAvailabilityStatus.DO_NOT_DISTURB)}
            disabled={isLoadingStatus || isChangingStatus}
          >
            <Text size="xs">{AVAILABILITY_STATUS_LABELS[ProviderAvailabilityStatus.DO_NOT_DISTURB]}</Text>
          </Menu.Item>

          <Divider />

          <Menu.Item leftSection={<IconLock size={16} />} component={Link} to="/changepassword">
            <Text size="xs">Change Password</Text>
          </Menu.Item>

          <Menu.Item leftSection={<IconLogout size={16} />} onClick={handleSignOut}>
            <Text size="xs">Sign Out</Text>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Flex>
  );
}
